import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type PortalHoverCardProps = {
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  gap?: number;
  anchorDirection?: 'above' | 'below';
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: React.ReactNode;
};

export function PortalHoverCard({
  anchorRef,
  open,
  gap = 12,
  anchorDirection = 'above',
  onMouseEnter,
  onMouseLeave,
  children,
}: PortalHoverCardProps): React.ReactElement | null {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number }>({ left: 0, top: 0 });

  const updatePosition = useCallback(() => {
    const anchorEl = anchorRef.current;
    const contentEl = contentRef.current;
    if (!anchorEl || !contentEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const contentHeight = contentEl.offsetHeight;

    const top =
      anchorDirection === 'above'
        ? Math.round(rect.top - contentHeight - gap)
        : Math.round(rect.bottom + gap);

    setPosition({ left: Math.round(rect.left), top });
  }, [anchorRef, gap, anchorDirection]);

  useLayoutEffect(() => {
    if (!open) return;
    const raf = window.requestAnimationFrame(updatePosition);
    return () => window.cancelAnimationFrame(raf);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handler = () => updatePosition();
    window.addEventListener('resize', handler, { passive: true });
    window.addEventListener('scroll', handler, { passive: true });
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler);
    };
  }, [open, updatePosition]);

  if (!open) return null;

  return createPortal(
    <div
      ref={contentRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ position: 'fixed', left: position.left, top: position.top, zIndex: 2000 }}
    >
      {children}
    </div>,
    document.body,
  ) as unknown as React.ReactElement;
}
